/**
 * QRCodeModal - Display QR code for tournament sharing
 */
import React, { useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import firebaseSyncService from '../../services/firebaseSync';
import './QRCodeModal.css';

const QRCodeModal = ({ isOpen, onClose }) => {
  const [copied, setCopied] = useState(false);
  const { isSignedIn } = useAuth();
  const { showSuccess } = useToast();

  if (!isOpen) return null;

  const tournamentId = firebaseSyncService.currentTournamentId;
  const hasActiveTournament = isSignedIn() && tournamentId;

  // Generate share URL
  const baseUrl = window.location.origin;
  const shareUrl = hasActiveTournament
    ? `${baseUrl}/view-tournament.html?id=${tournamentId}`
    : null;

  const handleCopyUrl = async () => {
    if (!shareUrl) return;

    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      showSuccess('URL copiée dans le presse-papier');

      // Reset copied state after 2 seconds
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy URL:', error);
    }
  };

  const handleDownloadQR = () => {
    // Get the SVG element
    const svg = document.querySelector('#qr-code-svg');
    if (!svg) return;

    // Convert SVG to blob
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);

      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `qr-code-tournoi-${tournamentId}.png`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        showSuccess('QR Code téléchargé');
      });
    };

    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  return (
    <>
      {/* Modal backdrop */}
      <div
        className="modal-backdrop fade show"
        style={{ zIndex: 1040 }}
        onClick={onClose}
      ></div>

      {/* Modal */}
      <div
        className="modal fade show d-block qrcode-modal"
        tabIndex="-1"
        role="dialog"
        style={{ zIndex: 1050 }}
      >
        <div className="modal-dialog modal-dialog-centered" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                <i className="bi bi-qr-code me-2"></i>
                Partager le tournoi
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={onClose}
                aria-label="Close"
              ></button>
            </div>

            <div className="modal-body">
              {!hasActiveTournament ? (
                <div className="qrcode-warning">
                  <h6>
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    Aucun tournoi actif
                  </h6>
                  <p>
                    Vous devez être connecté et avoir un tournoi en cours pour générer un QR code de partage.
                  </p>
                </div>
              ) : (
                <>
                  <p className="qrcode-description">
                    Scannez ce QR code ou partagez l'URL pour permettre à d'autres personnes de visualiser le tournoi en temps réel.
                  </p>

                  {/* QR Code */}
                  <div className="qrcode-wrapper">
                    <div className="qrcode-frame">
                      <QRCodeSVG
                        id="qr-code-svg"
                        value={shareUrl}
                        size={256}
                        level="H"
                        includeMargin={true}
                      />
                    </div>
                  </div>

                  {/* URL Input */}
                  <div>
                    <label className="qrcode-url-label">URL de partage</label>
                    <div className="qrcode-url-group">
                      <input
                        type="text"
                        className="qrcode-url-input"
                        value={shareUrl}
                        readOnly
                        onClick={(e) => e.target.select()}
                      />
                      <button
                        type="button"
                        className={`qrcode-copy-btn ${copied ? 'copied' : ''}`}
                        onClick={handleCopyUrl}
                      >
                        <i className={`bi ${copied ? 'bi-check-lg' : 'bi-clipboard'}`}></i>
                        <span className="d-none d-sm-inline">
                          {copied ? 'Copié !' : 'Copier'}
                        </span>
                      </button>
                    </div>
                  </div>

                  {/* Info Box */}
                  <div className="qrcode-info">
                    <h6>
                      <i className="bi bi-info-circle me-2"></i>
                      Informations
                    </h6>
                    <ul>
                      <li>Les spectateurs verront le tournoi en temps réel</li>
                      <li>Aucune modification n'est possible (lecture seule)</li>
                      <li>Le QR code reste valide tant que le tournoi existe</li>
                    </ul>
                  </div>
                </>
              )}
            </div>

            <div className="modal-footer">
              {hasActiveTournament && (
                <button
                  type="button"
                  className="qrcode-btn-download"
                  onClick={handleDownloadQR}
                >
                  <i className="bi bi-download me-2"></i>
                  Télécharger QR Code
                </button>
              )}
              <button
                type="button"
                className="qrcode-btn-close"
                onClick={onClose}
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default QRCodeModal;
